// MIDAS V3 — Markdown helpers and component renderers (shared)
//
// Lifted from ChatBotMessageV3.tsx so that both:
//   • ChatBotMessageV3      — final, persisted assistant messages
//   • StreamingMarkdownV3   — progressive token stream prose
// can render with the SAME ReactMarkdown component overrides.
//
// Keeping the two surfaces visually identical is the whole point of the lift
// — without it, the prose would visually "pop" the moment streaming ended
// and the final message rendered with different styling. See the streaming
// parity bug fix that introduced this module.
//
// Behaviour copied VERBATIM (no logic changes) from the previous inline
// implementation in ChatBotMessageV3.tsx:
//   • preprocessMarkdown — normalises bullets and re-stitches lines under
//     bold "headers" the bot emits as plain text instead of markdown.
//   • rewriteListRefsToSentinels + splitSentinelString — preserve <list-ref>
//     tags across markdown blocks (notably pipe-table cells) by collapsing
//     them to a single-token sentinel that survives the markdown parser.
//   • buildMarkdownComponents — V3-token-styled p / strong / h1-3 / a / ul /
//     ol / li / code / pre / table / thead / th / td / hr renderers, with
//     inline `<CiteV3>` substitution at sentinel sites.
//
// 2026-05-28 — MIDAS-LIVE-REF: extended with parallel sentinel pipelines for
//   • <live-ref address="…" value="…">…</live-ref> → <LiveRefV3>
//   • <val>TRUE|FALSE|…</val>                       → <ValV3>
// Mirrors the existing <list-ref> mechanism so the new tags also survive
// pipe-table cells. See LIVE_REF_INTEGRATION_SPEC.md Steps 3 & 4.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens. Plain inline styles only.
import React from "react";

import CiteV3 from "./CiteV3";
import { LiveRefV3, ValV3, type LiveValue } from "./LiveRefV3";
import { TelRefV3 } from "./TelRefV3";
import { CompRefV3 } from "./CompRefV3";
// DIAG-OVERLAY-B: the `pages` parse rule is shared with the click delegate in
// ChatBotMessageV3 (which re-reads the list off the chip's data attribute), so
// it lives in the neutral utils layer rather than being duplicated here.
import { parseCompRefPages } from "../../utils/compRefFlash";

// ═══════════════════════════════════════════════════════════════
//  Markdown pre-processing — verbatim from V2 (logic only)
// ═══════════════════════════════════════════════════════════════

const isBoldHeaderLine = (line: string): boolean =>
  /^\*\*.+\*\*$/.test(line.trim());
const isAlreadyListItem = (line: string): boolean =>
  /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line);
const isMarkdownHeading = (line: string): boolean =>
  /^#{1,6}\s+/.test(line.trim());

export const preprocessMarkdown = (text: string): string => {
  if (!text) return "";
  let processed = text.split("\\r\\n").join("\n").split("\\n").join("\n");
  processed = processed.replace(/^[•●◦▪▸►]\s*/gm, "- ");
  const lines = processed.split("\n");
  const result: string[] = [];
  let afterBoldHeader = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    if (isBoldHeaderLine(trimmed)) {
      afterBoldHeader = true;
      result.push(line);
    } else if (afterBoldHeader && trimmed.length > 0) {
      if (isAlreadyListItem(trimmed) || isMarkdownHeading(trimmed)) {
        result.push(line);
      } else {
        result.push(`- ${trimmed}`);
      }
    } else {
      if (trimmed.length === 0) afterBoldHeader = false;
      result.push(line);
    }
  }
  return result.join("\n");
};

// ═══════════════════════════════════════════════════════════════
//  Sentinel transform — identical to V2's approach so list-refs
//  inside pipe-table cells survive markdown parsing. We keep this
//  in v3 instead of importing from v2 because the v2 component is
//  the read-only reference per CLAUDE.md.
// ═══════════════════════════════════════════════════════════════

const SENTINEL_OPEN = "⟦"; // ⟦
const SENTINEL_CLOSE = "⟧"; // ⟧
// ASCII Unit Separator (U+001F). MUST be a real character — `String.split("")`
// degenerates to per-character split, which combined with `decodeURIComponent`
// throws URIError the moment a body byte lands on a `%` (production-blocking
// regression on /midas-v3 if the bot has ever emitted a <live-ref>). Mirrors
// V2's ChatBotMessageV2.tsx SENTINEL_SEP (verified byte-equal). Use the escape
// sequence so the literal stays visible in source even though the rendered
// character is non-printing.
const SENTINEL_SEP = "";
const LIST_REF_TAG_RE = /<list-ref\b([^>]*)>([\s\S]*?)<\/list-ref>/gi;
const LIVE_REF_TAG_RE = /<live-ref\b([^>]*)>([\s\S]*?)<\/live-ref>/gi;
// MIDAS-LIVE-REF follow-up (2026-05-28): the bot also emits a value-less PLC
// address reference — <plc-ref …>Q 41.3</plc-ref> — when it describes a signal
// it did NOT read live. There is NO `value=`. We funnel it into the SAME
// LIVEREF sentinel as <live-ref> (address = the PLC address, value = undefined)
// so it reuses the entire existing decode/split/render path; LiveRefV3's
// pipColor already returns var(--muted) (grey) for a missing value — the
// correct "no live value" state.
//
// PROVENANCE (2026-07-13, bot cross-team MACHINEID_TODO.md): the shared
// sentinel now carries a trailing source field ("live" | "plc") so the chip
// can gate the LIVE pill strictly on the live class. A <plc-ref> is a static
// PLC symbol-catalog citation and renders a plain "PLC" mini-label — the FE
// previously mislabelled it with the animated LIVE pill.
//
// ATTRIBUTE RENAME (2026-06-20, PLC-MARKUP-TAGCONTRACT-1): the bot contract is
// renaming <plc-ref>'s address attribute `bit=` → `address=` so it shares one
// attribute name with <live-ref> (BOT_CAPABILITIES.md:703,712 — "key the
// plc-ref chip variant on address=, tolerate legacy bit= during the
// transition"). Wire state TODAY is still `bit=`; the rename is queued, not yet
// on the wire. We read BOTH names forward-compatibly (prefer `address` when
// present, fall back to legacy `bit`) so this frontend can ship BEFORE the bot
// flips and keep working through and after the flip. The regex below captures
// attributes generically ([^>]*), so it already matches both <plc-ref bit="…">
// and <plc-ref address="…"> — only the attribute READ (see rewriteLiveRefsToSentinels)
// needed to change.
const PLC_REF_TAG_RE = /<plc-ref\b([^>]*)>([\s\S]*?)<\/plc-ref>/gi;
const VAL_TAG_RE = /<val\b([^>]*)>([\s\S]*?)<\/val>/gi;
// MIDAS V3 telemetry provenance citations (MASH-CITE-CHIPS, 2026-06-26):
// <tel-ref signal_key="part" window="this_month" count="10386">label</tel-ref>
// (windowed) and <tel-ref signal_key="run_state" window="current">label</tel-ref>
// (snapshot — count OMITTED). Unlike <plc-ref>/<live-ref>, this carries THREE
// semantic fields (signal_key/window/count) instead of address/value, so it
// gets its OWN sentinel kind (TELREF) + its own chip (<TelRefV3>) rather than
// funnelling into LIVEREF. The generic `[^>]*` capture matches both windowed
// and snapshot forms identically; the count-present/absent fork is resolved at
// attribute-read time (decodeTelRefBody), not in the regex.
const TEL_REF_TAG_RE = /<tel-ref\b([^>]*)>([\s\S]*?)<\/tel-ref>/gi;
// MIDAS V3 diagram-component chips (DIAG-OVERLAY-B, bot ticket
// DIAG-COMPONENT-CHIPS-1, 2026-08-21):
//   <comp-ref component="SQ431"  pages="1043">SQ431</comp-ref>
//   <comp-ref component="XB1:00" pages="1043,1049">XB1:00</comp-ref>
// `component` is a hotspot component_id EXACTLY as served by
// GET /api/v1/diagram/{page}/hotspots; `pages` is a comma-separated list of
// GLOBAL overlay page numbers, ascending — ALWAYS parsed as a list, even for a
// single value. Like <tel-ref>, this carries semantics no existing sentinel can
// express (a component id + an N-page list), so it gets its OWN sentinel kind
// (COMPREF) and its own chip (<CompRefV3>) instead of funnelling into LISTREF.
// The generic `[^>]*` capture matches the 1-page and N-page forms identically.
const COMP_REF_TAG_RE = /<comp-ref\b([^>]*)>([\s\S]*?)<\/comp-ref>/gi;
const LIST_DOUBLE_BRACKET_RE = /\[\[list:(\d+)\]\]/gi;
// MIDAS V3 alarm citations (2026-06-20): the bot emits a Sinumerik alarm-page
// reference as <page-ref page="N">…</page-ref> (shorthand [[page:N]]) — a tag
// DISTINCT from <list-ref>. It has the SAME job as <list-ref>: clicking it must
// open the page IMAGE in the artifact dock. We funnel it into the SAME LISTREF
// sentinel (listId='' , name=undefined, label=`Page N`) so it reuses the entire
// existing splitSentinel → CiteV3 → image-viewer path. A new sentinel KIND would
// be silently dropped — ANY_SENTINEL_RE only matches LISTREF|LIVEREF|VAL.
//
// FIX 4 (2026-06-20): The bot contract (BOT_CAPABILITIES.md:705, BOT_TODO.md:483,
// PLC_CHAIN_GROUND_TRUTH.md:1796) defines `<page-ref page>` with a required `page`
// attribute but NEVER shows a concrete closing form — only the shorthand
// `[[page:N]] → <page-ref page>`. Unlike <list-ref>/<live-ref> (which the contract
// shows with inner text), <page-ref> may legitimately arrive in any of three shapes:
//   • paired        <page-ref page="156">Page 156</page-ref>
//   • self-closing  <page-ref page="156" />
//   • open-only     <page-ref page="156">
// The OLD regex `<page-ref\b([^>]*)>([\s\S]*?)<\/page-ref>` matched ONLY the paired
// form, so self-closing and open-only tags fell through as raw markup. Worse, an
// `[\s\S]*?` body on an open-only tag (no closing tag) would, if a LATER </page-ref>
// ever appeared, swallow everything in between. We split into two regexes:
//   PAGE_REF_PAIRED_RE   — paired, captures inner label (mirrors <list-ref>).
//   PAGE_REF_VOID_RE     — self-closing OR open-only, NO body capture (cannot
//                          swallow following text). Runs AFTER the paired pass so
//                          a paired tag is never half-consumed by the void matcher.
const PAGE_REF_PAIRED_RE = /<page-ref\b([^>]*?)>([\s\S]*?)<\/page-ref>/gi;
const PAGE_REF_VOID_RE = /<page-ref\b([^>]*?)\/?>/gi;
const PAGE_DOUBLE_BRACKET_RE = /\[\[page:(\d+)\]\]/gi;

const readAttr = (attrs: string, name: string): string | undefined => {
  // Word-boundary hardening (TEL_REF_RENDERER_SPEC Finding #3): the old `\b`
  // anchor matched a name as a SUFFIX of a longer name — `readAttr(attrs,
  // "window")` would match the `window` tail of `data-window="x"` (the `-`
  // before `window` is a non-word char, so `\b` matched there). Anchor the
  // name to a REAL attribute boundary instead: it must start the attr string
  // or follow whitespace or a `/` (self-close). We do NOT use lookbehind
  // (Safari / older-target support). The `attrs` blob passed in is the tag's
  // generic `([^>]*)` capture, which begins with the char right after
  // `<tag-name` (typically a space), so the FIRST attribute is preceded by
  // that leading space and every later attribute by inter-attribute whitespace
  // — single-word names (page/id/name/address/value/bit/signal_key/window/
  // count) match exactly as before; only the `data-`-prefixed suffix case is
  // now correctly rejected.
  const re = new RegExp(
    `(?:^|[\\s/])${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`,
    "i"
  );
  const m = attrs.match(re);
  if (!m) return undefined;
  return m[1] ?? m[2] ?? m[3];
};

const buildSentinel = (
  page: number,
  listId: string,
  name: string | undefined,
  label: string
): string | null => {
  if (!Number.isFinite(page) || page <= 0) return null;
  const encName = name ? encodeURIComponent(name) : "";
  const encLabel = encodeURIComponent(label);
  return `${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}${page}${SENTINEL_SEP}${encodeURIComponent(
    listId || ""
  )}${SENTINEL_SEP}${encName}${SENTINEL_SEP}${encLabel}${SENTINEL_CLOSE}`;
};

export const rewriteListRefsToSentinels = (text: string): string => {
  if (!text) return "";

  let out = text.replace(LIST_REF_TAG_RE, (full, attrs, rawLabel) => {
    const label = (rawLabel || "").trim();
    const pageStr = readAttr(attrs || "", "page");
    const listId = readAttr(attrs || "", "id") ?? "";
    const name = readAttr(attrs || "", "name");
    const page = pageStr !== undefined ? Number(pageStr) : NaN;
    const sentinel = buildSentinel(page, listId, name, label);
    return sentinel ?? full;
  });

  // Strip surrounding `[…]` brackets that hug a sentinel.
  out = out.replace(
    new RegExp(
      `\\[\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\\]`,
      "g"
    ),
    "$1"
  );

  // Strip surrounding backticks that hug a sentinel.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  // `[[list:NNN]]` shorthand.
  out = out.replace(LIST_DOUBLE_BRACKET_RE, (_full, num) => {
    const page = Number(num);
    const listId = String(num).padStart(3, "0");
    const label = `List ${listId}`;
    const sentinel = buildSentinel(page, listId, undefined, label);
    return sentinel ?? `[[list:${num}]]`;
  });

  return out;
};

// MIDAS V3 alarm citations (2026-06-20). Mirrors rewriteListRefsToSentinels but
// for <page-ref page="N">…</page-ref> / [[page:N]]. Funnels into the SAME LISTREF
// sentinel — listId='' , name=undefined, label=`Page N` — so the chip clicks
// through the unchanged splitSentinelString → CiteV3 → image-viewer path. We do
// NOT invent a new sentinel kind (ANY_SENTINEL_RE would drop it to text). The
// `page` field carries the alarm page number; CiteV3 falls back to the label so
// the chip reads "Page 156" (not "List 156").
export const rewritePageRefsToSentinels = (text: string): string => {
  if (!text) return "";

  // Build the `Page N` chip sentinel for one <page-ref>. Shared by the paired
  // and void passes so the label/parse logic lives in one place.
  const pageRefSentinel = (attrs: string, full: string): string => {
    const pageStr = readAttr(attrs || "", "page");
    const page = pageStr !== undefined ? Number(pageStr) : NaN;
    const label = `Page ${page}`;
    const sentinel = buildSentinel(page, "", undefined, label);
    return sentinel ?? full;
  };

  // Pass 1 — PAIRED <page-ref …>label</page-ref>. The inner label is ignored
  // (we always derive `Page N` from the attribute, matching list-ref behaviour
  // where the chip text is canonical, not the bot's inner text).
  let out = text.replace(PAGE_REF_PAIRED_RE, (full, attrs, _rawLabel) =>
    pageRefSentinel(attrs, full)
  );

  // Pass 2 — VOID forms: self-closing `<page-ref page="N" />` and open-only
  // `<page-ref page="N">`. Runs AFTER pass 1, so any paired tag has already
  // been collapsed to a sentinel and cannot be re-matched here (the sentinel
  // contains no `<page-ref`). This pass captures NO body, so it can never
  // swallow text that follows the tag.
  out = out.replace(PAGE_REF_VOID_RE, (full, attrs) =>
    pageRefSentinel(attrs, full)
  );

  // Strip surrounding `[…]` brackets that hug a page-ref sentinel.
  out = out.replace(
    new RegExp(
      `\\[\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\\]`,
      "g"
    ),
    "$1"
  );

  // Strip surrounding backticks that hug a page-ref sentinel.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  // `[[page:NNN]]` shorthand.
  out = out.replace(PAGE_DOUBLE_BRACKET_RE, (_full, num) => {
    const page = Number(num);
    const label = `Page ${page}`;
    const sentinel = buildSentinel(page, "", undefined, label);
    return sentinel ?? `[[page:${num}]]`;
  });

  return out;
};

// ═══════════════════════════════════════════════════════════════
//  Live-ref + Val sentinel rewrites (MIDAS-LIVE-REF, 2026-05-28)
//
//  Mirror the <list-ref> approach so the new tags also survive
//  ReactMarkdown's parser (including pipe-table cells).
//
//  Sentinel formats:
//    ⟦LIVEREF␟<encAddress>␟<encValue>␟<encLabel>␟<source>⟧
//    ⟦VAL␟<encText>⟧
//
//  The trailing <source> field ("live" | "plc") was added 2026-07-13
//  (bot cross-team bug, MACHINEID_TODO.md): <plc-ref> is a STATIC PLC
//  symbol-catalog citation, NOT a live read, and must NOT render the
//  LIVE pill. The wire distinguishes the two tag classes; this field
//  carries that distinction through the shared LIVEREF sentinel so the
//  chip can gate the LIVE indicator strictly on source === "live".
//
//  Both attributes / inner text are encodeURIComponent'd so the
//  delimiter character (Unit Separator U+001F) can never appear in
//  a payload field.
// ═══════════════════════════════════════════════════════════════

const buildLiveRefSentinel = (
  address: string,
  value: string | undefined,
  label: string,
  source: "live" | "plc"
): string => {
  const encAddress = encodeURIComponent(address);
  const encValue = value !== undefined ? encodeURIComponent(value) : "";
  const encLabel = encodeURIComponent(label);
  // `source` is a closed literal ("live" | "plc") — URI-safe by construction,
  // no encoding needed.
  return `${SENTINEL_OPEN}LIVEREF${SENTINEL_SEP}${encAddress}${SENTINEL_SEP}${encValue}${SENTINEL_SEP}${encLabel}${SENTINEL_SEP}${source}${SENTINEL_CLOSE}`;
};

const buildValSentinel = (text: string): string => {
  const encText = encodeURIComponent(text);
  return `${SENTINEL_OPEN}VAL${SENTINEL_SEP}${encText}${SENTINEL_CLOSE}`;
};

// Telemetry provenance sentinel: ⟦TELREF␟<encKey>␟<encWin>␟<encCount>␟<encLabel>⟧
//
// Carries FOUR encoded fields (signal_key, window, count, label). The count
// field is the windowed-vs-snapshot branch point #1: an ABSENT `count`
// attribute (count === undefined) encodes to an EMPTY field, which
// decodeTelRefBody maps back to undefined → snapshot rendering downstream.
// This mirrors how buildLiveRefSentinel encodes an absent `value` as "".
const buildTelRefSentinel = (
  signalKey: string,
  window: string,
  count: string | undefined, // raw attr string, or undefined when absent
  label: string
): string => {
  const encKey = encodeURIComponent(signalKey);
  const encWin = encodeURIComponent(window);
  const encCount = count !== undefined ? encodeURIComponent(count) : ""; // empty = snapshot
  const encLabel = encodeURIComponent(label);
  return `${SENTINEL_OPEN}TELREF${SENTINEL_SEP}${encKey}${SENTINEL_SEP}${encWin}${SENTINEL_SEP}${encCount}${SENTINEL_SEP}${encLabel}${SENTINEL_CLOSE}`;
};

// Diagram-component sentinel: ⟦COMPREF␟<encComponent>␟<encPages>␟<encLabel>⟧
//
// The page LIST is comma-joined inside ONE encoded field (spec §3.1) rather
// than spread across variable-arity fields — the decoder splits a fixed 3-field
// body, so a 1-page and a 5-page chip have byte-identical sentinel SHAPES and
// the field count can never drift. encodeURIComponent leaves `,` untouched,
// but it does escape the `:` in ids like `XB1:00`, so the component id must be
// encoded (and safeDecode'd back) like every other payload field.
//
// Returns null on a malformed input (no component / empty page list) so the
// caller can leave the raw tag VERBATIM — same guard as plc-ref / tel-ref.
const buildCompRefSentinel = (
  component: string,
  pages: number[],
  label: string
): string | null => {
  if (!component || pages.length === 0) return null;
  const encComponent = encodeURIComponent(component);
  const encPages = encodeURIComponent(pages.join(","));
  const encLabel = encodeURIComponent(label);
  return `${SENTINEL_OPEN}COMPREF${SENTINEL_SEP}${encComponent}${SENTINEL_SEP}${encPages}${SENTINEL_SEP}${encLabel}${SENTINEL_CLOSE}`;
};

export const rewriteLiveRefsToSentinels = (text: string): string => {
  if (!text) return "";
  let out = text.replace(LIVE_REF_TAG_RE, (full, attrs, rawLabel) => {
    const address = readAttr(attrs || "", "address");
    if (!address) return full; // malformed — leave verbatim so it's visible
    const value = readAttr(attrs || "", "value");
    const label = ((rawLabel || "") as string).trim() || address;
    // <live-ref> = an actual read_live machine read — the ONLY class that may
    // carry the LIVE pill downstream (bot cross-team 2026-07-13).
    return buildLiveRefSentinel(address, value, label, "live");
  });

  // <plc-ref address="…"> (new) / <plc-ref bit="…"> (legacy) — value-less PLC
  // reference. Funnelled into the SAME LIVEREF sentinel with value=undefined so
  // it reuses the live-ref decode/render path and renders as a grey-pip chip.
  // Run AFTER <live-ref> so the two tag families don't interfere; they never nest.
  //
  // Forward-compatible attribute read (PLC-MARKUP-TAGCONTRACT-1): prefer the new
  // `address=` name, fall back to legacy `bit=`. This keeps the chip working
  // before, during, and after the bot's bit→address rename without a coordinated
  // deploy. (BOT_CAPABILITIES.md:703,712 — "tolerate legacy bit= during the
  // transition".)
  out = out.replace(PLC_REF_TAG_RE, (full, attrs, rawLabel) => {
    const addr = readAttr(attrs || "", "address") ?? readAttr(attrs || "", "bit");
    if (!addr) return full; // malformed (neither address nor bit) — leave verbatim so it's visible
    const label = ((rawLabel || "") as string).trim() || addr;
    // value omitted → buildLiveRefSentinel emits an empty value field →
    // decodeLiveRefBody maps "" back to undefined → grey "no live value" pip.
    // source="plc" (bot cross-team 2026-07-13): a <plc-ref> is a STATIC PLC
    // symbol-catalog citation — the chip renders a plain "PLC" mini-label,
    // never the LIVE pill.
    return buildLiveRefSentinel(addr, undefined, label, "plc");
  });

  // Strip surrounding backticks that hug a sentinel — same defensive pattern
  // as the list-ref pipeline (the bot occasionally wraps inline values in `…`).
  // Covers both <live-ref>- and <plc-ref>-derived LIVEREF sentinels.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}LIVEREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  return out;
};

export const rewriteValsToSentinels = (text: string): string => {
  if (!text) return "";
  let out = text.replace(VAL_TAG_RE, (full, _attrs, rawText) => {
    const inner = ((rawText || "") as string).trim();
    if (!inner) return full;
    return buildValSentinel(inner);
  });

  // Strip surrounding backticks that hug a val sentinel.
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}VAL${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  return out;
};

// Telemetry provenance rewrite (MASH-CITE-CHIPS, 2026-06-26). Clones
// rewriteLiveRefsToSentinels' structure but emits the TELREF sentinel (its own
// kind) since <tel-ref> carries signal_key/window/count rather than
// address/value. Malformed tags (missing signal_key or window) are left
// VERBATIM so the (rare, bot-bug) case stays visible for reporting rather than
// rendering an empty chip — matches the <plc-ref>/<live-ref> guard.
export const rewriteTelRefsToSentinels = (text: string): string => {
  if (!text) return "";
  let out = text.replace(TEL_REF_TAG_RE, (full, attrs, rawLabel) => {
    const signalKey = readAttr(attrs || "", "signal_key");
    const window = readAttr(attrs || "", "window");
    if (!signalKey || !window) return full; // malformed — leave verbatim so it's visible
    const count = readAttr(attrs || "", "count"); // undefined on snapshot form
    const label =
      ((rawLabel || "") as string).trim() || `${signalKey} (${window})`;
    return buildTelRefSentinel(signalKey, window, count, label);
  });

  // Strip surrounding backticks that hug a TELREF sentinel (defensive; same as
  // the live-ref pipeline — the bot occasionally wraps inline chips in `…`).
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}TELREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  return out;
};

// Diagram-component rewrite (DIAG-OVERLAY-B). Clones rewriteTelRefsToSentinels'
// structure but emits the COMPREF sentinel (its own kind) since <comp-ref>
// carries a component id + an N-page list rather than address/value or
// signal_key/window/count.
//
// Malformed tags are left VERBATIM so a bot bug stays visible rather than
// rendering a dead chip — matches the <plc-ref>/<tel-ref> guard. "Malformed"
// here means any of: missing `component`, missing `pages`, or a `pages` value
// that parses to an EMPTY list (e.g. `pages=""`, `pages="abc"`, `pages="0"`).
export const rewriteCompRefsToSentinels = (text: string): string => {
  if (!text) return "";
  let out = text.replace(COMP_REF_TAG_RE, (full, attrs, rawLabel) => {
    const component = readAttr(attrs || "", "component");
    const pagesAttr = readAttr(attrs || "", "pages");
    if (!component || pagesAttr === undefined) return full; // malformed
    const pages = parseCompRefPages(pagesAttr);
    if (pages.length === 0) return full; // malformed (no usable page)
    const label = ((rawLabel || "") as string).trim() || component;
    return buildCompRefSentinel(component, pages, label) ?? full;
  });

  // Strip surrounding backticks that hug a COMPREF sentinel (defensive; same
  // as the live-ref / tel-ref pipelines — the bot occasionally wraps inline
  // component ids in `…`, which would otherwise render the chip inside a
  // <code> block).
  out = out.replace(
    new RegExp(
      `\`\\s*(${SENTINEL_OPEN}COMPREF${SENTINEL_SEP}[^${SENTINEL_CLOSE}]+${SENTINEL_CLOSE})\\s*\``,
      "g"
    ),
    "$1"
  );

  return out;
};

/**
 * Compose all three sentinel rewrites in the order the spec requires:
 *   1. live-refs first  — most likely to be nested inside other prose.
 *   2. val tags second  — they live in the surrounding prose, not inside
 *      live-refs, so order is largely cosmetic; doing it after live-refs
 *      avoids any accidental overlap if the bot ever wraps a val in a
 *      live-ref's inner text.
 *   3. list-refs last   — pre-existing behaviour, untouched.
 *
 * Exposed so callers can chain a single rewrite pass instead of remembering
 * the order. Both ChatBotMessageV3 and any future surface (StreamingMarkdownV3
 * if it ever needs sentinels) should go through this.
 */
export const rewriteAllRefsToSentinels = (text: string): string => {
  if (!text) return "";
  // page-refs run LAST. They funnel into the same LISTREF sentinel as list-refs,
  // so running after rewriteListRefsToSentinels keeps the two passes independent
  // (neither rewrites the other's emitted sentinel — the tag regexes only match
  // the literal <page-ref>/<list-ref> source, not the ⟦LISTREF…⟧ output).
  // tel-ref runs alongside the live-ref family. It never nests in / wraps the
  // other tags (its own TELREF sentinel kind is distinct from LIVEREF/VAL/
  // LISTREF), so order relative to the others is independent — placed inside
  // the live-ref family for clarity. After Task 1 this means
  // rewriteAllRefsToSentinels collapses <tel-ref> everywhere it's used,
  // including the streaming surface (Task 2) for free.
  // comp-ref (DIAG-OVERLAY-B) is likewise order-INDEPENDENT: <comp-ref> is a
  // distinct tag with its own COMPREF sentinel kind, it never nests inside and
  // is never nested inside the other families, and no pass rewrites another
  // pass's emitted sentinel (the tag regexes only match literal `<tag …>`
  // source). Placed innermost purely so the diff reads as one added layer.
  return rewritePageRefsToSentinels(
    rewriteListRefsToSentinels(
      rewriteTelRefsToSentinels(
        rewriteValsToSentinels(
          rewriteLiveRefsToSentinels(rewriteCompRefsToSentinels(text))
        )
      )
    )
  );
};

const SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}LISTREF${SENTINEL_SEP}(\\d+)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Live-ref sentinel: ⟦LIVEREF␟<encAddress>␟<encValue>␟<encLabel>⟧
const LIVE_REF_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}LIVEREF${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_SEP}]*)${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Val sentinel: ⟦VAL␟<encText>⟧
const VAL_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}VAL${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Telemetry provenance sentinel: ⟦TELREF␟<encKey>␟<encWin>␟<encCount>␟<encLabel>⟧
const TEL_REF_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}TELREF${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Diagram-component sentinel: ⟦COMPREF␟<encComponent>␟<encPages>␟<encLabel>⟧
const COMP_REF_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}COMPREF${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

// Combined matcher so a single linear scan can split a text run into
// alternating text / list-ref / live-ref / val / tel-ref / comp-ref segments
// without re-walking the string. Note: per-type regexes above are kept for the
// `code` unwrap path which only cares about a single sentinel type at a time.
//
// ⚠ EVERY sentinel kind MUST appear in this alternation. A kind that is missing
// here is NOT matched by the scan, so it falls through to the `else` text
// branch in splitSentinelString and the chip LEAKS RAW as a Unicode sentinel
// string in the user's prose. TELREF and COMPREF were each added here in the
// same commit that introduced them — keep that discipline.
const ANY_SENTINEL_RE = new RegExp(
  `${SENTINEL_OPEN}(LISTREF|LIVEREF|VAL|TELREF|COMPREF)${SENTINEL_SEP}([^${SENTINEL_CLOSE}]*)${SENTINEL_CLOSE}`,
  "g"
);

interface SentinelPayload {
  page: number;
  listId: string;
  name?: string;
  label: string;
}

interface LiveRefPayload {
  address: string;
  value: string | undefined;
  label: string;
  /**
   * Chip provenance class (bot cross-team 2026-07-13): "live" = actual
   * read_live machine read (<live-ref>) — the only class that renders the
   * LIVE pill; "plc" = static PLC symbol-catalog citation (<plc-ref>).
   */
  source: "live" | "plc";
}

interface TelRefPayload {
  signalKey: string;
  window: string;
  count: number | undefined; // undefined = snapshot form (or malformed count)
  label: string;
}

export interface CompRefPayload {
  /** Hotspot component_id exactly as served by the diagram hotspots endpoint. */
  component: string;
  /** Global overlay page numbers. ALWAYS a list — never empty (an empty parse
   *  leaves the tag verbatim upstream, so no COMPREF sentinel is ever built). */
  pages: number[];
  label: string;
}

export type SentinelSplitItem =
  | { kind: "text"; text: string }
  | { kind: "ref"; payload: SentinelPayload }
  | { kind: "live"; payload: LiveRefPayload }
  | { kind: "val"; payload: { text: string } }
  | { kind: "tel"; payload: TelRefPayload }
  | { kind: "comp"; payload: CompRefPayload };

// decodeURIComponent throws URIError on a malformed sequence (e.g. a lone
// `%` not followed by two hex digits). All sentinel payload fields are
// encodeURIComponent'd on the way in, so the round-trip is normally safe —
// but a malformed bot attribute (or a future tag we funnel in) must NOT be
// able to throw and white-screen /midas-v3. Degrade to the raw string so the
// chip still renders (showing the un-decoded address) instead of crashing.
const safeDecode = (s: string): string => {
  if (!s) return "";
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

const decodeListRefBody = (body: string): SentinelPayload => {
  const fields = body.split(SENTINEL_SEP);
  const page = Number(fields[0]);
  const listId = safeDecode(fields[1] || "");
  const rawName = safeDecode(fields[2] || "");
  const label = safeDecode(fields[3] || "") || `List ${listId || page}`;
  return {
    page,
    listId,
    name: rawName || undefined,
    label,
  };
};

const decodeLiveRefBody = (body: string): LiveRefPayload => {
  const fields = body.split(SENTINEL_SEP);
  const address = safeDecode(fields[0] || "");
  const rawValue = safeDecode(fields[1] || "");
  const label = safeDecode(fields[2] || "") || address;
  // Fail-closed provenance (bot cross-team 2026-07-13): the LIVE pill is a
  // positive claim that a value was read off the running machine. Decode to
  // "live" ONLY on the explicit marker; anything else (missing field, unknown
  // value) degrades to the static "plc" rendering — never a false LIVE.
  const source: "live" | "plc" = fields[3] === "live" ? "live" : "plc";
  return {
    address,
    value: rawValue === "" ? undefined : rawValue,
    label,
    source,
  };
};

const decodeValBody = (body: string): { text: string } => {
  return { text: safeDecode(body || "") };
};

// Windowed-vs-snapshot branch point #2 (TIGHTENED — TEL_REF_RENDERER_SPEC
// Finding #2): the count routes to the WINDOWED form ONLY if it is a non-empty,
// non-negative INTEGER. We trim FIRST, then require rawCount !== "" AND
// Number.isInteger(parsed) && parsed >= 0. Everything else — missing, empty,
// whitespace-only, fractional ("1.5"), negative, or non-numeric ("abc") —
// degrades to undefined → snapshot ("latest snapshot") rather than a broken
// "n=" number. The old Number.isFinite check wrongly accepted "1.5" (a
// fractional sample count is meaningless) and, since Number("   ") === 0,
// risked a whitespace count reading as n=0.
const decodeTelRefBody = (body: string): TelRefPayload => {
  const fields = body.split(SENTINEL_SEP);
  const signalKey = safeDecode(fields[0] || "");
  const window = safeDecode(fields[1] || "");
  const rawCount = safeDecode(fields[2] || "").trim(); // trim FIRST (Finding #2)
  const label = safeDecode(fields[3] || "") || `${signalKey} (${window})`;
  const parsed = Number(rawCount);
  const isWindowed =
    rawCount !== "" && Number.isInteger(parsed) && parsed >= 0;
  return {
    signalKey,
    window,
    count: isWindowed ? parsed : undefined,
    label,
  };
};

// Diagram-component decode (DIAG-OVERLAY-B). Three fixed fields; the middle
// one is the comma-joined page list, re-parsed with the SAME
// parseCompRefPages() the rewrite used, so a corrupted sentinel degrades the
// same way a corrupted attribute does (empty list) instead of producing NaN
// pages that would silently never match a tab.
const decodeCompRefBody = (body: string): CompRefPayload => {
  const fields = body.split(SENTINEL_SEP);
  const component = safeDecode(fields[0] || "");
  const pages = parseCompRefPages(safeDecode(fields[1] || ""));
  const label = safeDecode(fields[2] || "") || component;
  return { component, pages, label };
};

/**
 * Coerce the raw bot-emitted `value` attribute into the type LiveRefV3
 * accepts. Per spec Step 3 ¶4:
 *   "TRUE" → true, "FALSE" → false, otherwise pass through as string
 *   (don't pre-coerce to number — the chip's pip-color logic accepts
 *   string-with-units like "1.23 bar" per handoff README §Visual spec).
 */
const parseLiveRefValue = (raw: string | undefined): LiveValue => {
  if (raw === undefined) return undefined;
  if (raw === "TRUE") return true;
  if (raw === "FALSE") return false;
  return raw;
};

// Exported for unit testing (TEL_REF_RENDERER_SPEC §10) — pure, no React.
// Lets a test round-trip a rewritten buffer to the decoded payloads without
// reaching into private sentinel internals. Production callers reach it via
// renderChildrenWithSentinels.
export const splitSentinelString = (s: string): SentinelSplitItem[] => {
  const out: SentinelSplitItem[] = [];
  if (!s) return out;
  let lastIndex = 0;
  ANY_SENTINEL_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ANY_SENTINEL_RE.exec(s)) !== null) {
    if (m.index > lastIndex) {
      out.push({ kind: "text", text: s.slice(lastIndex, m.index) });
    }
    const tag = m[1];
    const body = m[2] || "";
    if (tag === "LISTREF") {
      out.push({ kind: "ref", payload: decodeListRefBody(body) });
    } else if (tag === "LIVEREF") {
      out.push({ kind: "live", payload: decodeLiveRefBody(body) });
    } else if (tag === "VAL") {
      out.push({ kind: "val", payload: decodeValBody(body) });
    } else if (tag === "TELREF") {
      out.push({ kind: "tel", payload: decodeTelRefBody(body) });
    } else if (tag === "COMPREF") {
      out.push({ kind: "comp", payload: decodeCompRefBody(body) });
    } else {
      // Unknown tag — fall through to text so we never silently drop content.
      out.push({ kind: "text", text: m[0] });
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < s.length) {
    out.push({ kind: "text", text: s.slice(lastIndex) });
  }
  return out;
};

const renderChildrenWithSentinels = (
  children: React.ReactNode,
  resolveActive: (page: number) => boolean
): React.ReactNode => {
  if (children == null) return children;
  if (typeof children === "string") {
    const parts = splitSentinelString(children);
    if (parts.length === 0) return "";
    if (parts.length === 1 && parts[0].kind === "text") return parts[0].text;
    return parts.map((p, i) => {
      if (p.kind === "text") {
        return <React.Fragment key={i}>{p.text}</React.Fragment>;
      }
      if (p.kind === "live") {
        // LiveRefV3 owns its own tooltip + onClick placeholder. anim defaults
        // to "glow" — LOCKED app-wide per LIVE_REF_INTEGRATION_SPEC.md Step 6;
        // do NOT pass an override here. `value` is coerced from the raw
        // attribute via parseLiveRefValue: "TRUE"/"FALSE" → boolean, anything
        // else passes through as a string for the chip's pip-color logic.
        // `source` gates the pill truthfully: "live" (<live-ref>) → LIVE pill;
        // "plc" (<plc-ref>) → static "PLC" catalog chip, no live styling.
        return (
          <LiveRefV3
            key={i}
            address={p.payload.address}
            value={parseLiveRefValue(p.payload.value)}
            source={p.payload.source}
          />
        );
      }
      if (p.kind === "val") {
        // Companion to LiveRefV3 — TRUE / FALSE / numeric text with matching
        // red/green/accent semantic. Bot doesn't emit <val> reliably yet;
        // parser is in place so it lights up automatically when it does.
        return <ValV3 key={i}>{p.payload.text}</ValV3>;
      }
      if (p.kind === "tel") {
        // Telemetry provenance chip (TEL_REF_RENDERER_SPEC). Own component +
        // own tooltip. count===undefined → snapshot rendering inside TelRefV3.
        return (
          <TelRefV3
            key={i}
            signalKey={p.payload.signalKey}
            window={p.payload.window}
            count={p.payload.count}
            label={p.payload.label}
          />
        );
      }
      if (p.kind === "comp") {
        // Diagram-component chip (DIAG-OVERLAY-B). Own component + own
        // tooltip. The CLICK is NOT wired here — CompRefV3 publishes
        // data-comp-id / data-comp-pages and ChatBotMessageV3's message-level
        // onClickCapture delegate runs the flash/blink algorithm, because that
        // algorithm needs `message.artifacts` which this renderer never sees.
        return (
          <CompRefV3
            key={i}
            component={p.payload.component}
            pages={p.payload.pages}
            label={p.payload.label}
          />
        );
      }
      // p.kind === "ref" — existing <list-ref> → <CiteV3> path. Unchanged.
      const isActive = resolveActive(p.payload.page);
      return (
        <CiteV3
          key={i}
          page={p.payload.page}
          label={p.payload.label}
          tooltip={p.payload.name}
        >
          {/* Active state — bolder background fill. Plain chip otherwise. */}
          <span
            style={
              isActive
                ? {
                    background: "var(--accent)",
                    color: "var(--card)",
                    padding: "0 4px",
                    borderRadius: 3,
                    margin: "-1px -5px",
                  }
                : undefined
            }
          >
            {p.payload.label}
          </span>
        </CiteV3>
      );
    });
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => (
      <React.Fragment key={i}>
        {renderChildrenWithSentinels(c, resolveActive)}
      </React.Fragment>
    ));
  }
  return children;
};

// ═══════════════════════════════════════════════════════════════
//  v3 markdown renderers — inline-styled with v3 tokens. No Tailwind.
// ═══════════════════════════════════════════════════════════════

export const buildMarkdownComponents = (
  resolveActive: (page: number) => boolean
): object => {
  const renderText = (children: React.ReactNode) =>
    renderChildrenWithSentinels(children, resolveActive);

  return {
    p: ({ children }: any) => (
      <p
        style={{
          margin: "0 0 8px 0",
          fontSize: 14,
          lineHeight: 1.5,
          color: "var(--ink)",
          fontFamily: "var(--font-sans)",
        }}
      >
        {renderText(children)}
      </p>
    ),
    strong: ({ children }: any) => (
      <strong style={{ fontWeight: 600, color: "var(--ink)" }}>
        {renderText(children)}
      </strong>
    ),
    em: ({ children }: any) => (
      <em style={{ fontStyle: "italic" }}>{renderText(children)}</em>
    ),
    h1: ({ children }: any) => (
      <h1
        style={{
          margin: "10px 0 6px 0",
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.3,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2
        style={{
          margin: "10px 0 6px 0",
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1.3,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3
        style={{
          margin: "10px 0 6px 0",
          fontSize: 14,
          fontWeight: 700,
          lineHeight: 1.3,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </h3>
    ),
    a: ({ href, children }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "var(--accent)", textDecoration: "none" }}
      >
        {renderText(children)}
      </a>
    ),
    ul: ({ children }: any) => (
      <ul style={{ margin: "6px 0", paddingLeft: 18, listStyle: "disc" }}>
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol style={{ margin: "6px 0", paddingLeft: 18, listStyle: "decimal" }}>
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li
        style={{
          margin: "3px 0",
          fontSize: 13,
          lineHeight: 1.5,
          color: "var(--ink)",
        }}
      >
        {renderText(children)}
      </li>
    ),
    code: ({ inline, children, className }: any) => {
      // react-markdown 9 (this project's version) REMOVED the `inline` prop —
      // the `code` renderer is called for BOTH inline and fenced code with
      // `inline === undefined`. Relying on `if (inline)` therefore sent EVERY
      // inline code (e.g. a bot symbol name like `I_DOOR_CLOSE`) down the
      // `<pre>` branch, rendering it as a full-width grey block inside table
      // cells and prose (actual2/actual3 screenshots). Detect inline-vs-block
      // from the real v9 signals instead:
      //   • a fenced block carries a `language-*` className (from the info
      //     string) and/or spans multiple lines;
      //   • inline code has neither.
      // If a future react-markdown restores the `inline` prop, honour it.
      const codeText =
        typeof children === "string"
          ? children
          : Array.isArray(children) &&
            children.length === 1 &&
            typeof children[0] === "string"
          ? children[0]
          : null;
      const hasLanguageClass = /language-/.test(className || "");
      const isMultiline = codeText != null && codeText.includes("\n");
      const isInline =
        inline === true ||
        (inline === undefined && !hasLanguageClass && !isMultiline);

      if (isInline) {
        // If the inline code wraps ONLY a sentinel, unwrap so the chip renders
        // as a button / chip, not as `<code><button/></code>` (same bug v2 fixed
        // for list-refs; mirrored here for live-refs + val tags).
        if (typeof children === "string") {
          const trimmed = children.trim();
          // ⚠ Twin of the ANY_SENTINEL_RE alternation above: a sentinel kind
          // missing from BOTH lists below stays wrapped in <code> and renders
          // as raw sentinel text instead of a chip. Add every new kind here.
          const isAnySentinel =
            (trimmed.startsWith(SENTINEL_OPEN + "LISTREF") ||
              trimmed.startsWith(SENTINEL_OPEN + "LIVEREF") ||
              trimmed.startsWith(SENTINEL_OPEN + "VAL") ||
              trimmed.startsWith(SENTINEL_OPEN + "TELREF") ||
              trimmed.startsWith(SENTINEL_OPEN + "COMPREF")) &&
            trimmed.endsWith(SENTINEL_CLOSE);
          if (isAnySentinel) {
            const stripped = trimmed
              .replace(SENTINEL_RE, "")
              .replace(LIVE_REF_SENTINEL_RE, "")
              .replace(VAL_SENTINEL_RE, "")
              .replace(TEL_REF_SENTINEL_RE, "")
              .replace(COMP_REF_SENTINEL_RE, "");
            if (stripped.trim() === "") {
              return <>{renderText(children)}</>;
            }
          }
        }
        return (
          <code
            className="mono"
            style={{
              // Force inline so it can never be painted as a full-width block,
              // even nested in a table cell (the bug this fix targets).
              display: "inline",
              width: "auto",
              whiteSpace: "normal",
              verticalAlign: "baseline",
              fontSize: 12.5,
              color: "var(--ink-2)",
              background: "var(--card-2)",
              // SP-1V: slightly softer, rounder chip to match the §1A reference
              // code chips (`ddraw.ini`, `windowed=true`).
              padding: "1.5px 5px",
              borderRadius: 4,
            }}
          >
            {renderText(children)}
          </code>
        );
      }
      return (
        <pre
          className="mono"
          style={{
            margin: "8px 0",
            maxHeight: 240,
            overflow: "auto",
            padding: 12,
            background: "var(--card-2)",
            border: "1px solid var(--border-soft)",
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          <code className={className}>{children}</code>
        </pre>
      );
    },
    table: ({ children }: any) => (
      <div style={{ overflowX: "auto", margin: "10px 0" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
          }}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead style={{ borderBottom: "1px solid var(--border)" }}>
        {children}
      </thead>
    ),
    th: ({ children }: any) => (
      <th
        className="mono"
        style={{
          padding: "8px 10px",
          textAlign: "left",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--faint)",
        }}
      >
        {renderText(children)}
      </th>
    ),
    td: ({ children }: any) => (
      <td
        style={{
          padding: "8px 10px",
          borderBottom: "1px solid var(--border-soft)",
          verticalAlign: "top",
          color: "var(--ink-2)",
        }}
      >
        {renderText(children)}
      </td>
    ),
    hr: () => (
      <hr
        style={{
          margin: "12px 0",
          border: 0,
          borderTop: "1px dashed var(--border)",
        }}
      />
    ),
  };
};
